import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/agency-questions");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/agency-questions")({
  head: () => ({
    meta: [
        { name: "description", content: "The 12 questions every Colorado business owner should ask before hiring an SEO or web design agency. Filter out the fluff and find a real partner." },
      {
        title: "12 Questions to Ask Any Colorado Marketing Agency",
      },
      {
        name: "description",
        content:
          "The buyer&rsquo;s-guide checklist that exposes outsourcing, white-labeling, and AI-bot agencies in 5 minutes.",
      },
      {
        property: "og:title",
        content: "12 Questions to Ask Any Colorado SEO Agency",
      },
      {
        property: "og:description",
        content:
          "Steal this checklist before you sign anything.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/agency-questions` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/agency-questions" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="8 min read"
      title="12 questions to ask before hiring any Colorado SEO or web design agency"
      intro="If you&rsquo;ve ever gotten an AI-bot email saying your website &ldquo;sucks,&rdquo; or a cold call from someone who clearly knows nothing about your business, you already know how bad this industry can be. Here&rsquo;s the checklist that exposes the outsourcers, the white-labelers, and the bot farms in 12 questions."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "12 questions to ask" },
      ]}
      path="/learn/agency-questions"
      faqs={[
        { q: "What's the most important question to ask a marketing agency?", a: "'Who actually does the work?' If the answer is anything other than a named in-house person, you're paying agency markup for outsourced or whitelabeled work." },
        { q: "Should I sign a long-term contract with a marketing agency?", a: "Month-to-month is the new standard for honest agencies. Long contracts protect the agency, not you. If they're confident in the results, they don't need to lock you in." },
        { q: "How do I know if an agency is being honest about results?", a: "Ask for raw Google Analytics, Search Console, and Google Business Profile screenshots — not just their custom dashboard. Honest agencies show you the source data." },
      ]}
    >
      <h2>Print this. Take it to your next agency call.</h2>
      <p>
        Watch how they answer — not just <em>what</em> they answer. Hesitation, vagueness, and
        &ldquo;great question, let me get back to you&rdquo; are answers in themselves.
      </p>

      <h2>The 12 questions</h2>
      <ol>
        <li>
          <strong>Who, by name, will actually do the work on my account?</strong>
          <br />Look for: a real person, in-house, ideally introduced before you sign.
          Red flag: &ldquo;our team&rdquo; with no names.
        </li>
        <li>
          <strong>Are any pieces white-labeled or outsourced overseas?</strong>
          <br />A lot of TV-station marketing arms, &ldquo;media&rdquo; companies, and franchise
          agencies sell SEO and web design that&rsquo;s actually fulfilled by a third party in
          another country. You deserve to know.
        </li>
        <li>
          <strong>Can I see 3 current Colorado clients I can call directly?</strong>
          <br />Not testimonials. Not case studies. Real phone numbers of real local clients.
        </li>
        <li>
          <strong>Who owns the website, the domain, the hosting, and the analytics?</strong>
          <br />The only acceptable answer is <strong>you do</strong>. If they own anything,
          you&rsquo;re hostage.
        </li>
        <li>
          <strong>What&rsquo;s your monthly retainer scope, in writing?</strong>
          <br />A real agency can hand you a one-pager listing exactly what hours/deliverables
          you get each month.
        </li>
        <li>
          <strong>What&rsquo;s your 30-day cancellation policy?</strong>
          <br />Anything longer than a 30-day notice on an ongoing service is a trap. (One-time
          builds are different.)
        </li>
        <li>
          <strong>Do you guarantee any rankings?</strong>
          <br />Correct answer: <em>no.</em> Anyone who says yes is lying. Google says so directly.
        </li>
        <li>
          <strong>How do you measure success — leads, calls, or vanity metrics?</strong>
          <br />Watch for &ldquo;impressions&rdquo; and &ldquo;keywords ranked.&rdquo; Demand
          phone calls, form fills, and booked jobs.
        </li>
        <li>
          <strong>Can I see a sample monthly report?</strong>
          <br />If they can&rsquo;t produce one in 24 hours, they don&rsquo;t do real reporting.
        </li>
        <li>
          <strong>What happens if I&rsquo;m unhappy with month 3?</strong>
          <br />A real agency will tell you exactly. A bad one will deflect.
        </li>
        <li>
          <strong>How much of your business comes from referrals vs cold outreach?</strong>
          <br />Healthy local agencies are mostly referrals. Cold-call/AI-bot shops are mostly
          outbound — and that&rsquo;s usually because clients leave.
        </li>
        <li>
          <strong>Will I work directly with the owner at any point?</strong>
          <br />For small businesses under $25K projects, the answer should be yes. At our agency,
          you work with Chris (the owner) start to finish.
        </li>
      </ol>

      <h2>Two bonus questions if they cold-emailed you saying your site is broken</h2>
      <ul>
        <li><strong>Did you actually run an audit of my site, or did an AI tool generate this email?</strong></li>
        <li><strong>What specifically is broken? Show me on screen.</strong></li>
      </ul>
      <p>
        90% of those &ldquo;your website sucks&rdquo; emails are from automated tools that
        don&rsquo;t even render your real page. They&rsquo;re scare tactics designed to book a
        sales call, not honest feedback.
      </p>

      <h2>Related reading</h2>
      <ul>
        <li>
          <Link to="/honest-answers/red-flags" className="font-bold text-brand-gold-deep underline">
            10 red flags when hiring a Colorado SEO or web design agency
          </Link>
        </li>
        <li>
          <Link to="/honest-answers/website-cost" className="font-bold text-brand-gold-deep underline">
            How much a Colorado website really costs
          </Link>
        </li>
        <li>
          <Link to="/honest-answers/not-a-fit" className="font-bold text-brand-gold-deep underline">
            When we&rsquo;re NOT the right fit
          </Link>
        </li>
      </ul>
    </ArticleLayout>
  );
}
